// src/components/modals/ObjectDetailsModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import "./ObjectDetailsModal.css";
import "./ObjectExplorerModals.css";

export default function ObjectDetailsModal({
  show,
  onHide,
  object,
  bucketName,
  onDeleteRequest,
  onShareRequest,
}) {
  // Hooks devem estar sempre no topo
  const [previewUrl, setPreviewUrl] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const token = localStorage.getItem("token");
  const expires = 3600; // válida por 1 hora

  const isImage = (key) => /\.(jpg|jpeg|png|gif|webp)$/i.test(key);
  const isPDF = (key) => /\.pdf$/i.test(key);

  // Gera URL pré-assinada para preview
  useEffect(() => {
    if (!show || !object || !(isImage(object.Key) || isPDF(object.Key))) {
      setPreviewUrl("");
      return;
    }
    setLoadingPreview(true);

    const shareUrl = `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(
      object.Key
    )}?expires=${expires}`;

    axios
      .get(shareUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setPreviewUrl(res.data.url);
      })
      .catch((err) => {
        console.error("Erro ao gerar preview:", err);
      })
      .finally(() => {
        setLoadingPreview(false);
      });
  }, [show, object, bucketName, token]);

  // Se não há objeto, não renderiza nada
  if (!object) return null;

  const handleDownload = async () => {
    try {
      let url = previewUrl;
      if (!url) {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(
            object.Key
          )}?expires=${expires}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        url = res.data.url;
      }
      window.open(url, "_blank");
    } catch (err) {
      console.error("Erro ao baixar arquivo:", err);
      alert("Não foi possível baixar o arquivo");
    }
  };

  const formatSize = (size) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  };

  const hasPreview = isImage(object.Key) || isPDF(object.Key);
  const objectName = object.Key.split("/").filter(Boolean).pop() || object.Key;

  return (
    <Modal show={show} onHide={onHide} centered size="xl" className="object-modal object-details-modal">
      <Modal.Header closeButton>
        <Modal.Title className="object-modal-title">
          Detalhes do Objeto
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="object-details">
        <div className={`object-details-layout ${hasPreview ? "with-preview" : ""}`}>
          <section className="object-details-main">
            <div className="object-summary-card">
              <span className="object-summary-label">Arquivo</span>
              <h3 title={objectName}>{objectName}</h3>
              <p title={object.Key}>{object.Key}</p>
            </div>

            <div className="details-grid">
              <div className="info-pair" style={{ gridColumn: "1 / -1" }}>
                <strong>Nome completo</strong>
                <span className="break-word">{object.Key}</span>
              </div>
              <div className="info-pair">
                <strong>ETAG</strong>
                <span>{object.ETag || "N/A"}</span>
              </div>
              <div className="info-pair">
                <strong>Última modificação</strong>
                <span>
                  {new Date(object.LastModified).toLocaleString("pt-BR")}
                </span>
              </div>
              <div className="info-pair">
                <strong>Tamanho</strong>
                <span>{formatSize(object.Size)}</span>
              </div>
              <div className="info-pair">
                <strong>Tags</strong>
                <span>{object.Tags || "N/A"}</span>
              </div>
              <div className="info-pair">
                <strong>Legal Hold</strong>
                <span>{object.LegalHold || "Off"}</span>
              </div>
              <div className="info-pair">
                <strong>Retention</strong>
                <span>{object.Retention || "None"}</span>
              </div>
              <div className="info-pair" style={{ gridColumn: "1 / -1" }}>
                <strong>Metadata</strong>
                <pre>
                  {object.Metadata
                    ? JSON.stringify(object.Metadata, null, 2)
                    : "N/A"}
                </pre>
              </div>
            </div>
          </section>

          <aside className="object-details-preview-panel">
            <div className="preview-panel-header">
              <span>Preview</span>
              <strong>{hasPreview ? "Disponível" : "Indisponível"}</strong>
            </div>

            {hasPreview ? (
              <div className="object-preview side-preview">
                {loadingPreview ? (
                  <div
                    style={{ height: 200 }}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <Spinner animation="border" />
                  </div>
                ) : isImage(object.Key) ? (
                  <img
                    src={previewUrl}
                    alt="preview"
                    style={{ maxWidth: "100%" }}
                  />
                ) : (
                  <iframe
                    src={previewUrl}
                    width="100%"
                    height="520px"
                    title="preview"
                  />
                )}
              </div>
            ) : (
              <div className="object-preview-empty">
                <div className="object-preview-empty-icon">•</div>
                <p>Pré-visualização não disponível para este tipo de arquivo.</p>
              </div>
            )}
          </aside>
        </div>
      </Modal.Body>

      <Modal.Footer className="object-details-buttons">
        <Button
          variant="danger"
          onClick={() => {
            onDeleteRequest(object.Key);
            onHide();
          }}
        >
          Excluir
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => {
            onShareRequest(object.Key);
            onHide();
          }}
        >
          Compartilhar
        </Button>
        <Button variant="primary" onClick={handleDownload}>
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
